import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  Typography,
  Stack,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  Chip,
} from '@mui/material';
import {
  IoChevronDown,
  IoPersonOutline,
  IoKeyOutline,
  IoLogOutOutline,
  IoReceiptOutline,
  IoSettingsOutline,
  IoCalculatorOutline,
  IoSchoolOutline,
} from 'react-icons/io5';
import { HiPlusCircle } from 'react-icons/hi2';
import { Menu as SzhMenu, MenuButton as SzhMenuButton } from '@szhsin/react-menu';
import { NotificationIcon } from 'src/images/svg';
import useResponsive from '../../hooks/useResponsive';
import { TransactionModal, Notifications } from 'src/components';
import { MyContext } from 'src/context';
import { toast } from 'react-toastify';
import { useAuth } from 'src/context/auth/AuthContext';
import { getRoleBaseRoute, getRoleLabel, hasRolePermission } from 'src/config/permissions';

interface AccountPopoverProps {
  user?: {
    firstname?: string;
    lastname?: string;
    email?: string;
    role?: string;
  };
}

export default function AccountPopover({ user }: AccountPopoverProps) {
  const myContext = useContext(MyContext);
  const setUser = myContext?.setUser;

  let currentUser: any = null;
  let signOutFn: any = null;
  try {
    const auth = useAuth();
    currentUser = auth?.user;
    signOutFn = auth?.signOut;
  } catch (e) {
    currentUser = null;
  }

  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    handleCloseMenu();
    navigate(path);
  };

  const handleLogout = async () => {
    handleCloseMenu();
    try {
      if (signOutFn) {
        await signOutFn();
      }
      if (setUser) {
        setUser(null);
      }
      navigate('/login', { replace: true });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const role = currentUser?.role?.toUpperCase();
  const routePrefix = getRoleBaseRoute(role);
  const canCreateTransaction = hasRolePermission(role, 'transactions.create');
  const displayName =
    currentUser?.user_metadata?.firstname ||
    currentUser?.displayName ||
    currentUser?.email?.split('@')[0] ||
    'Account';
  const fullName =
    currentUser?.user_metadata?.firstname && currentUser?.user_metadata?.lastname
      ? `${currentUser.user_metadata.firstname} ${currentUser.user_metadata.lastname}`
      : displayName;
  const userEmail = currentUser?.email || '';
  const photoUrl =
    currentUser?.dbUser?.photourl ||
    currentUser?.user_metadata?.avatar_url ||
    '';

  const roleBadgeLabel = getRoleLabel(role) || 'User';

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {/* 1. Quick Transaction Action (+) */}
      {canCreateTransaction && (routePrefix === '/admin' || isDesktop) && (
        <>
          <IconButton
            size="small"
            aria-label="New Transaction"
            onClick={() => setIsTxModalOpen(true)}
            sx={{
              p: 0.5,
              color: '#16A34A',
              '&:hover': { bgcolor: '#F0FDF4' },
            }}
          >
            <HiPlusCircle size={28} />
          </IconButton>
          <Box sx={{ width: 1, height: 20, bgcolor: '#E2E8F0', mx: 0.5 }} />
        </>
      )}

      {/* 2. Notifications Dropdown */}
      <SzhMenu
        menuButton={
          <SzhMenuButton
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
            }}
          >
            <Box
              aria-label="Notifications"
              sx={{
                p: 0.75,
                borderRadius: 1.5,
                color: '#475569',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
            >
              <NotificationIcon color="#475569" />
            </Box>
          </SzhMenuButton>
        }
        menuStyle={{
          borderRadius: 12,
          width: isDesktop ? 380 : 320,
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          marginTop: 8,
          border: '1px solid #E2E8F0',
        }}
      >
        <Notifications />
      </SzhMenu>

      <Box sx={{ width: 1, height: 20, bgcolor: '#E2E8F0', mx: 0.5 }} />

      {/* 3. Account Menu Trigger */}
      <ButtonBase
        onClick={handleOpenMenu}
        aria-label="Account menu"
        aria-controls={isMenuOpen ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        sx={{
          py: 0.5,
          px: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isMenuOpen ? '#CBD5E1' : '#E2E8F0',
          bgcolor: isMenuOpen ? '#F8FAFC' : '#FFFFFF',
          transition: 'all 0.15s ease',
          '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar
            src={photoUrl}
            alt={displayName}
            sx={{
              width: 30,
              height: 30,
              fontSize: '0.8rem',
              fontWeight: 700,
              bgcolor: '#0F172A',
              color: '#FFFFFF',
            }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ textAlign: 'left', minWidth: 50, maxWidth: 120 }}>
            <Typography
              noWrap
              variant="subtitle2"
              sx={{
                color: '#0F172A',
                fontSize: '0.8rem',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {displayName}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              sx={{
                color: '#64748B',
                fontSize: '0.675rem',
                fontWeight: 600,
                lineHeight: 1,
                display: 'block',
                textTransform: 'capitalize',
              }}
            >
              {role ? role.toLowerCase() : 'user'}
            </Typography>
          </Box>

          <IoChevronDown
            size={13}
            color="#64748B"
            style={{
              transition: 'transform 0.2s',
              transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Stack>
      </ButtonBase>

      {/* 4. Rich Account Settings & Navigation Popover Menu */}
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 230,
            maxWidth: 280,
            overflow: 'visible',
            filter: 'drop-shadow(0px 8px 24px rgba(15, 23, 42, 0.12))',
            mt: 1,
            borderRadius: 2,
            border: '1px solid #E2E8F0',
            p: 0.5,
          },
        }}
      >
        {/* User Identity Header */}
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '0.85rem' }} noWrap>
            {fullName}
          </Typography>
          {userEmail && (
            <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.725rem', mb: 0.75 }} noWrap>
              {userEmail}
            </Typography>
          )}
          <Chip
            label={roleBadgeLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: '#F1F5F9',
              color: '#334155',
              borderRadius: 1,
              border: '1px solid #E2E8F0',
            }}
          />
        </Box>

        <Divider sx={{ my: 0.5, borderColor: '#F1F5F9' }} />

        {/* Option 1: Profile Settings */}
        <MenuItem
          onClick={() => handleNavigate(`${routePrefix}/edit-profile`)}
          sx={{
            py: 0.75,
            px: 1.25,
            borderRadius: 1.25,
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#334155',
            '&:hover': { bgcolor: '#F8FAFC', color: '#0F172A' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: '#64748B' }}>
            <IoPersonOutline size={16} />
          </ListItemIcon>
          <ListItemText
            primary="Profile Settings"
            primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
          />
        </MenuItem>

        {/* Option 2: Change Password & Security */}
        <MenuItem
          onClick={() => handleNavigate(`${routePrefix}/change-password`)}
          sx={{
            py: 0.75,
            px: 1.25,
            borderRadius: 1.25,
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#334155',
            '&:hover': { bgcolor: '#F8FAFC', color: '#0F172A' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: '#64748B' }}>
            <IoKeyOutline size={16} />
          </ListItemIcon>
          <ListItemText
            primary="Change Password"
            primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
          />
        </MenuItem>

        {/* Option 3: Role Shortcuts */}
        {hasRolePermission(role, 'transactions.view_all') && (
          <MenuItem
            onClick={() => handleNavigate('/admin/transaction-status-tracker')}
            sx={{
              py: 0.75,
              px: 1.25,
              borderRadius: 1.25,
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#334155',
              '&:hover': { bgcolor: '#F8FAFC', color: '#0F172A' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: '#64748B' }}>
              <IoReceiptOutline size={16} />
            </ListItemIcon>
            <ListItemText
              primary="Transactions Tracker"
              primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
            />
          </MenuItem>
        )}

        {routePrefix === '/student' && (
          <MenuItem
            onClick={() => handleNavigate('/student/paychecks')}
            sx={{
              py: 0.75,
              px: 1.25,
              borderRadius: 1.25,
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#334155',
              '&:hover': { bgcolor: '#F8FAFC', color: '#0F172A' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: '#64748B' }}>
              <IoReceiptOutline size={16} />
            </ListItemIcon>
            <ListItemText
              primary="My Paychecks"
              primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
            />
          </MenuItem>
        )}

        <Divider sx={{ my: 0.5, borderColor: '#F1F5F9' }} />

        {/* Option 4: Sign Out */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 0.75,
            px: 1.25,
            borderRadius: 1.25,
            color: '#DC2626',
            '&:hover': { bgcolor: '#FEF2F2', color: '#B91C1C' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: '#EF4444' }}>
            <IoLogOutOutline size={17} />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 700, color: 'inherit' }}
          />
        </MenuItem>
      </Menu>

      <TransactionModal open={isTxModalOpen} closeModal={() => setIsTxModalOpen(false)} />
    </Stack>
  );
}
